import React, { useEffect, useState } from "react";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import {
  Button,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Paper,
  Table,
  TableContainer,
  Typography,
  IconButton,
  Tooltip,
  Container,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box } from "@mui/material";
import { Loading } from "../../../components/Loading";
import axios from "axios";
import { useToast } from "../../../components/Toast";
import AddParkingModal from "./AddParkingModal";
import NavigationHeader from "../../../components/NavigationHeader";
import { tableHeaderStyles, tableCellStyles } from "../../../utils/styles";

export interface Parking {
  id?: string;
  name: string;
  contactNumber: string;
  location: string;
  companyId?: string;
}

const Parking = () => {
  const [parkingLocations, setParkingLocations] = useState<Parking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedParking, setSelectedParking] = useState<Parking | undefined>();
  const { user, loading: authLoading } = useFirebaseAuth();
  const { showToast } = useToast();

  useEffect(() => {
    if (user && !authLoading) {
      fetchParkingData();
    }
  }, [user, authLoading]);

  const fetchParkingData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST}master-data/parkings`,
        {
          headers: {
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      if (response?.data?.isSuccess) {
        setParkingLocations(response?.data?.data);
      }
    } catch (error) {
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddParking = () => {
    setSelectedParking(undefined);
    setIsModalOpen(true);
  };

  const handleEditParking = (parking: Parking) => {
    setSelectedParking(parking);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedParking(undefined);
  };

  const handleSubmit = async (parkingData: any) => {
    setIsSubmitting(true);
    if (selectedParking) {
      // Update existing parking
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_HOST}master-data/parkings/${parkingData?.id}`,
          parkingData,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Parking location updated successfully", "success");
          fetchParkingData();
        } else {
          showToast(`Error updating parking location`, "error");
        }
      } catch (error) {
        console.error("Error updating parking location", error);
        showToast(`Error updating parking location`, "error");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Add new parking
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_HOST}master-data/parkings`,
          parkingData,
          {
            headers: {
              Authorization: `Bearer ${user?.accessToken}`,
            },
          }
        );
        if (response?.data?.isSuccess) {
          showToast("Parking location added successfully", "success");
          fetchParkingData();
        } else {
          showToast(`Error submitting data`, "error");
        }
      } catch (error) {
        console.error("Error adding parking location", error);
        showToast(`Error adding parking location`, "error");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (isLoading || authLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting..." fullScreen={false} />
    ) : (
      <Loading message="Loading parking locations..." fullScreen={false} />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Master Data", path: "/master-data" },
          { label: "Parking Locations" },
        ]}
        title="Parking Locations"
        button={{
          label: "Add Parking Location",
          onClick: handleAddParking,
          icon: <AddIcon />,
        }}
      />

      {parkingLocations.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
          No parking locations found
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="parking locations table">
            <TableHead>
              <TableRow>
                <TableCell {...tableHeaderStyles}>Name</TableCell>
                <TableCell {...tableHeaderStyles}>Contact Number</TableCell>
                <TableCell {...tableHeaderStyles}>Location</TableCell>
                <TableCell {...tableHeaderStyles} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parkingLocations.map((parking) => (
                <TableRow
                  key={parking.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell {...tableCellStyles}>{parking.name}</TableCell>
                  <TableCell {...tableCellStyles}>
                    {parking.contactNumber}
                  </TableCell>
                  <TableCell {...tableCellStyles}>
                    {parking.location || "-"}
                  </TableCell>
                  <TableCell {...tableCellStyles} align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditParking(parking)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddParkingModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        parking={selectedParking}
        isEdit={!!selectedParking}
      />
    </Container>
  );
};

export default Parking;
